<template>
  <div class="notes-component" :class="{'active': showEditor}">
    <section class="notes-buttons">
      <button class="writing-notes" @click="writeNotes" v-if="!showEditor">
        <span class="take-notes">
          Write
          <i class="fas fa-pen"></i>
        </span>
      </button>
      <button class="writing-notes success" v-if="showEditor" @click="saveNotes">
        <span>{{ savingButton }}</span>
      </button>
      <button class="reading-notes" v-if="notes && !showEditor" @click="readNotes">
        Read
        <i class="fas fa-glasses"></i>
      </button>
    </section>
    <transition name="fade" mode="in-out">
      <span class="save-notification" v-if="saving">Autosaving...</span>
    </transition>

    <section class="notes-field-edit" id="editor" v-show="showEditor"></section>

    <section class="notes-read" id="notes-read" v-show="showNotes">
      <div class="read-notes-buttons">
        <button @click="printNotes">
          <i class="fas fa-print"></i>
        </button>
        <button @click="showNotes = !showNotes">
          <i class="fas fa-window-close"></i>
        </button>
      </div>

      <div class="notes-output" v-html="outputText"></div>
    </section>
  </div>
</template>

<script>
import pell from "pell";
// import pellOptions from "@/helpers/pellHelper.js";
import * as cryptoJS from "crypto-js";
// import AES from "crypto-js/aes";
import firebase from "firebase/app";
// eslint-disable-next-line
import auth from "firebase/auth";
import { setTimeout } from "timers";

export default {
  name: "Notes",

  props: ["notes", "board"],

  data() {
    return {
      input: "",
      showEditor: false,
      outputText: null,
      pellEditor: null,
      userId: null,
      boardId: null,
      decryptedNotes: null,
      autoSaveNotes: null,
      saving: false,
      showNotes: false,
      savingInterval: 180000 // 3 mins
    };
  },

  computed: {
    savingButton() {
      if (this.notes) {
        return this.outputText === this.decryptedNotes ? "Close" : "Save";
      } else {
        return this.outputText === this.notes ? "Close" : "Save";
      }
    }
  },

  methods: {
    ensureHTTP: str => (/^https?:\/\//.test(str) && str) || `http://${str}`,

    printNotes() {
      const modal = this.outputText;
      const newWindow = window.open("", "Print");
      newWindow.document.write("<html><head><title>Notes</title></head><body>");
      newWindow.document.write(modal);
      newWindow.document.write("</body></html>");
      newWindow.document.close();
      newWindow.focus();
      newWindow.print();
    },

    readNotes() {
      this.showNotes = true;
      let decrypted = "<p></p>";
      if (this.notes && this.notes.length > 0) {
        this.decryptedNotes = this.decryptNotes(this.notes);
        decrypted = this.decryptedNotes;
      }
      this.outputText = decrypted;
    },

    writeNotes() {
      this.autoSaveNotes = setInterval(() => {
        if (this.showEditor) {
          this.saving = true;
          this.storeNotes();
          setTimeout(() => {
            this.saving = false;
          }, 3000);
        }
      }, this.savingInterval);

      let decrypted = "<p></p>";
      if (this.notes && this.notes.length > 0) {
        this.decryptedNotes = this.decryptNotes(this.notes);
        decrypted = this.decryptedNotes;
      }
      this.outputText = decrypted;
      this.pellEditor.content.innerHTML = this.outputText;
      this.showEditor = true;
    },

    saveNotes() {
      this.showEditor = false;
      // turn off autosave
      clearInterval(this.autoSaveNotes);
      this.storeNotes();
    },

    storeNotes() {
      const r = /<\w+>|<\/\w+>/g;
      let enc = "";

      if (this.notes) {
        if (this.decryptNotes(this.notes) !== this.outputText) {
          if (this.outputText.replace(r, "").length > 0) {
            enc = this.encryptNotes(this.outputText);
          }
          this.$emit("saveNotes", enc);
        }
      } else {
        if (this.outputText.replace(r, "").length > 0) {
          enc = this.encryptNotes(this.outputText);
        }
        this.$emit("saveNotes", enc);
      } // only save if new or changed
    },

    encryptNotes(notes) {
      let secret = "";
      if (this.userId) {
        secret = this.userId;
      } else {
        this.userId = this.board.userId;
        secret = this.userId;
      }

      // const encrypted = AES.encrypt(notes, secret).toString();
      const encrypted = cryptoJS.AES.encrypt(notes, secret).toString();
      return encrypted;
    },

    decryptNotes(notes) {
      let secret = "";
      if (this.userId) {
        secret = this.userId;
      } else {
        this.userId = this.board.userId;
        secret = this.userId;
      }
      // const bytes = AES.decrypt(notes, secret);
      const bytes = cryptoJS.AES.decrypt(notes, secret);
      const decrypted = bytes.toString(cryptoJS.enc.Utf8);
      return decrypted;
    }
  },

  mounted() {
    let user = firebase.auth().currentUser;
    if (user && user.uid === this.board.userId) {
      this.userId = user.uid;
    }

    this.pellEditor = pell.init({
      element: document.getElementById("editor"),
      defaultParagraphSeparator: "p",
      styleWithCSS: true,
      onChange: html => {
        this.outputText = html;
      },
      actions: [
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "heading1",
        "heading2",
        "paragraph",
        "line",
        {
          name: "image",
          icon:
            '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"  fill="rgb(90, 90, 90)" viewBox="0 0 24 24"><path d="M5 8.5c0-.828.672-1.5 1.5-1.5s1.5.672 1.5 1.5c0 .829-.672 1.5-1.5 1.5s-1.5-.671-1.5-1.5zm9 .5l-2.519 4-2.481-1.96-4 5.96h14l-5-8zm8-4v14h-20v-14h20zm2-2h-24v18h24v-18z"/></svg>',
          result: () => {
            const url = window.prompt("Enter the image URL");
            if (url) pell.exec("insertImage", this.ensureHTTP(url));
          }
        },
        {
          name: "link",
          icon:
            '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"  fill="rgb(90, 90, 90)" viewBox="0 0 24 24"><path d="M21.337.458l-3.937 3.936c-2.449-1.466-5.67-1.144-7.78.966l-4.261 4.262c-2.11 2.11-2.433 5.331-.968 7.78l-3.935 3.935c-.609.609-.609 1.597 0 2.207s1.598.609 2.207 0l3.935-3.935c2.449 1.465 5.67 1.144 7.78-.967l4.261-4.261c2.11-2.11 2.433-5.331.968-7.781l3.936-3.936c.609-.609.609-1.597 0-2.207s-1.597-.609-2.206.001zm-4.862 11.757l-4.261 4.261c-.895.894-2.176 1.169-3.31.827l1.632-1.632c.609-.609.609-1.597 0-2.207s-1.598-.609-2.207 0l-1.631 1.632c-.343-1.133-.067-2.415.826-3.309l4.261-4.261c.895-.894 2.176-1.169 3.31-.827l-1.631 1.63c-.609.609-.609 1.597 0 2.207s1.598.609 2.207 0l1.63-1.63c.343 1.133.067 2.415-.826 3.309z"/></svg>',
          result: () => {
            const url = window.prompt("Enter the link URL");
            if (url) pell.exec("createLink", this.ensureHTTP(url));
          }
        },
        {
          name: "fontSize",
          icon:
            '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="rgb(90, 90, 90)" viewBox="0 0 24 24"><path d="M24 20v1h-4v-1h.835c.258 0 .405-.178.321-.422l-.473-1.371h-2.231l-.575-1.59h2.295l-1.362-4.077-1.154 3.451-.879-2.498.921-2.493h2.222l3.033 8.516c.111.315.244.484.578.484h.469zm-6-1h1v2h-7v-2h.532c.459 0 .782-.453.633-.887l-.816-2.113h-6.232l-.815 2.113c-.149.434.174.887.633.887h1.065v2h-7v-2h.43c.593 0 1.123-.375 1.32-.935l5.507-15.065h3.952l5.507 15.065c.197.56.69.935 1.284.935zm-10.886-6h4.238l-2.259-6.199-1.979 6.199z"/></svg>',
          title: "Font size",
          result: () => {
            const size = window.prompt(
              "Choose the font size (xsmall, small, medium, large, xlarge)",
              "medium"
            );
            let selectedSize = "3";
            switch (size) {
              case "xsmall":
                selectedSize = "1";
                break;
              case "small":
                selectedSize = "2";
                break;
              case "medium":
                selectedSize = "3";
                break;
              case "large":
                selectedSize = "4";
                break;
              case "xlarge":
                selectedSize = "5";
                break;
              default:
                selectedSize = "3";
                break;
            }
            pell.exec("fontSize", selectedSize);
          }
        },
        {
          name: "unorderedList",
          icon:
            '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="rgb(90, 90, 90)"  viewBox="0 0 24 24"><path d="M7 1h17v2h-17v-2zm0 7h17v-2h-17v2zm0 5h17v-2h-17v2zm0 5h17v-2h-17v2zm0 5h17v-2h-17v2zm-5-22c-1.104 0-2 .896-2 2s.896 2 2 2 2-.896 2-2-.896-2-2-2zm0 9c-1.104 0-2 .896-2 2s.896 2 2 2 2-.896 2-2-.896-2-2-2zm0 9c-1.104 0-2 .896-2 2s.896 2 2 2 2-.896 2-2-.896-2-2-2z"/></svg>',
          title: "Unordered list",
          result: () => {
            pell.exec("insertUnorderedList");
          }
        },
        {
          name: "orderedList",
          icon:
            '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="rgb(90, 90, 90)" viewBox="0 0 24 24"><path d="M7 1h17v2h-17v-2zm0 7h17v-2h-17v2zm0 5h17v-2h-17v2zm0 5h17v-2h-17v2zm0 5h17v-2h-17v2zm-5.511-16h1.353v-6h-1.14c0 .91-.809 1.07-1.702 1.111v1h1.489v3.889zm2.433 6.667h-1.827c.823-.74 1.722-1.627 1.722-2.782 0-1.145-.762-1.885-1.941-1.885-.642 0-1.288.204-1.833.656l.423 1.148c.352-.279.715-.524 1.167-.524.486 0 .754.255.754.717-.008.774-.858 1.527-2.387 3.018v.958h3.922v-1.306zm-.87 6.124c.499-.266.771-.715.771-1.288 0-.748-.568-1.503-1.836-1.503-.571 0-1.241.142-1.74.482l.307 1.161c.432-.284.831-.394 1.15-.394.428 0 .674.174.674.478 0 .425-.52.538-.827.538h-.577v1.154h.601c.438 0 .949.183.949.701 0 .39-.329.633-.86.633-.451 0-.887-.163-1.282-.397l-.304 1.2c.5.322 1.11.444 1.675.444 1.344 0 2.247-.728 2.247-1.812 0-.646-.371-1.178-.948-1.397z"/></svg>',
          title: "Numbered list",
          result: () => {
            pell.exec("insertOrderedList");
          }
        },
        {
          name: "hiliteColor",
          icon:
            '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="rgb(90,90,90)" viewBox="0 0 24 24"><path d="M18.311 2.828l2.862 2.861-15.033 15.032-3.583.722.723-3.584 15.031-15.031zm0-2.828l-16.873 16.872-1.438 7.127 7.127-1.437 16.874-16.873-5.69-5.689z"/></svg>',
          title: "Highlight",
          result: () => {
            pell.exec("hiliteColor", "yellow");
          }
        },
        {
          name: "justifyCenter",
          icon:
            '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="rgb(90,90,90)" viewBox="0 0 24 24"><path d="M24 3h-24v-2h24v2zm-6 3h-12v2h12v-2zm6 5h-24v2h24v-2zm-6 5h-12v2h12v-2zm6 5h-24v2h24v-2z"/></svg>',
          title: "Center",
          result: () => {
            pell.exec("justifyCenter");
          }
        },
        {
          name: "justifyLeft",
          icon:
            '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="rgb(90,90,90)" viewBox="0 0 24 24"><path d="M24 3h-24v-2h24v2zm-12 3h-12v2h12v-2zm12 5h-24v2h24v-2zm-12 5h-12v2h12v-2zm12 5h-24v2h24v-2z"/></svg>',
          title: "Justify left",
          result: () => {
            pell.exec("justifyLeft");
          }
        },
        {
          name: "justifyRight",
          icon:
            '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="rgb(90,90,90)" viewBox="0 0 24 24"><path d="M0 1h24v2h-24v-2zm12 7h12v-2h-12v2zm-12 5h24v-2h-24v2zm12 5h12v-2h-12v2zm-12 5h24v-2h-24v2z"/></svg>',
          title: "Justify right",
          result: () => {
            pell.exec("justifyRight");
          }
        },
        {
          name: "formatBlock",
          icon:
            '<svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" fill="rgb(90,90,90)" clip-rule="evenodd"><path d="M20 15h4.071v2h-4.071v4.071h-2v-4.071h-4.071v-2h4.071v-4.071h2v4.071zm-8 6h-12v-2h12v2zm0-4.024h-12v-2h12v2zm0-3.976h-12v-2h12v2zm12-4h-24v-2h24v2zm0-4h-24v-2h24v2z"/></svg>',
          title: "Format element",
          result: () => {
            const element = window.prompt(
              "How would you like to format this? (code, blockquote, none)"
            );
            let selectedElement = "<p>";
            switch (element) {
              case "code":
                selectedElement = "<pre>";
                break;
              case "blockquote":
                selectedElement = "<blockquote>";
                break;
              case "none":
                selectedElement = "<p>";
                break;
              default:
                selectedElement = "<p>";
                break;
            }
            pell.exec("formatBlock", selectedElement);
          }
        },
        {
          name: "removeFormat",
          icon:
            '<svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" fill="rgb(90,90,90)" clip-rule="evenodd"><path d="M19 14.586l3.586-3.586 1.414 1.414-3.586 3.586 3.586 3.586-1.414 1.414-3.586-3.586-3.586 3.586-1.414-1.414 3.586-3.586-3.586-3.586 1.414-1.414 3.586 3.586zm-7 6.414h-12v-2h12v2zm0-4.024h-12v-2h12v2zm0-3.976h-12v-2h12v2zm12-4h-24v-2h24v2zm0-4h-24v-2h24v2z"/></svg>',
          title: "Remove format",
          result: () => {
            pell.exec("removeFormat", "");
          }
        }
      ],
      classes: {
        selected: "pell-button-selected"
      }
    });
  }
};
</script>

